import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "../config/api";
import { useAuth } from "../context/AuthContext";
import { contactChannelService } from "../services/contactChannelService";
import { productService } from "../services/productService";
import { profilerService } from "../services/profilerService";
import {
	normalizeNicheCategoryState,
	normalizeProductsResponse,
} from "../utils/productManagement";
import {
	AlertCircle,
	ArrowRight,
	BadgeCheck,
	CheckCircle,
	Image as ImageIcon,
	Loader2,
	MapPin,
	MessageCircle,
	Package,
	Phone,
	RefreshCw,
	Save,
	Send,
	ShieldAlert,
	Store,
} from "lucide-react";

const EMPTY_FORM = {
	shopName: "",
	storeAddress: "",
	note: "",
	profilePicture: "",
};

const EMPTY_CONTACT_FORM = {
	PHONE_NUMBER: "",
	WHATSAPP: "",
	TELEGRAM: "",
};

const normalizeContactChannels = (data) => {
	if (Array.isArray(data)) {
		return data;
	}

	if (Array.isArray(data?.channels)) {
		return data.channels;
	}

	if (Array.isArray(data?.items)) {
		return data.items;
	}

	if (Array.isArray(data?.data)) {
		return data.data;
	}

	return [];
};

const getChannelTimestamp = (channel) => {
	const timestamp = Date.parse(channel?.updatedAt || channel?.createdAt || "");
	return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getLatestChannelByType = (channels, type) =>
	channels
		.filter((channel) => String(channel?.type || "").toUpperCase() === type)
		.slice()
		.sort((left, right) => getChannelTimestamp(right) - getChannelTimestamp(left))[0] ||
	null;

const extractTelegramRegisterLink = (responseData) =>
	responseData?.telegramRegisterLink ||
	responseData?.data?.telegramRegisterLink ||
	"";

const buildProfilePatch = (seller, formData) => {
	const payload: Record<string, string> = {};

	const nextShopName = formData.shopName.trim();
	const nextStoreAddress = formData.storeAddress.trim();
	const nextNote = formData.note.trim();
	const nextProfilePicture = formData.profilePicture.trim();

	if (nextShopName !== (seller?.shopName || "")) {
		payload.shopName = nextShopName;
	}

	if (nextStoreAddress !== (seller?.storeAddress || "")) {
		payload.storeAddress = nextStoreAddress;
	}

	if (nextNote !== (seller?.note || "")) {
		payload.note = nextNote;
	}

	if (nextProfilePicture !== (seller?.profilePicture || "")) {
		payload.profilePicture = nextProfilePicture;
	}

	return payload;
};

const resolveTelegramState = (channel) => {
	if (
		!channel?.value &&
		!channel?.telegramRegisterLink &&
		!channel?.profilerTelegramChatId
	) {
		return { label: "Not configured", tone: "slate" };
	}

	if (channel?.profilerTelegramChatId) {
		return { label: "Connected", tone: "emerald" };
	}

	return { label: "Pending verification", tone: "amber" };
};

const TELEGRAM_STATUS_STYLES = {
	slate:
		"border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300",
	emerald:
		"border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300",
	amber:
		"border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300",
};

export default function Profile() {
	const { user, refreshProfile } = useAuth();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const token = user?.token;
	const hasSellerAccount = Boolean(user?.seller?.id || user?.sellerAccountId);

	const [formData, setFormData] = useState(EMPTY_FORM);
	const [contactForm, setContactForm] = useState(EMPTY_CONTACT_FORM);
	const [submitError, setSubmitError] = useState("");
	const [submitSuccess, setSubmitSuccess] = useState("");
	const [contactError, setContactError] = useState("");
	const [contactSuccess, setContactSuccess] = useState("");
	const [activeChannelType, setActiveChannelType] = useState("");
	const [isRefreshingTelegram, setIsRefreshingTelegram] = useState(false);
	const [telegramRegisterLinkOverride, setTelegramRegisterLinkOverride] =
		useState("");

	const profileQuery = useQuery({
		queryKey: ["profiler-account-me"],
		queryFn: async () => profilerService.getMe(token),
		enabled: Boolean(token) && hasSellerAccount,
	});

	const nicheQuery = useQuery({
		queryKey: ["my-niche-categories"],
		queryFn: async () =>
			normalizeNicheCategoryState(await productService.getMyNicheCategories()),
		enabled: Boolean(token) && hasSellerAccount,
	});

	const productsQuery = useQuery({
		queryKey: ["seller-products", "profile"],
		queryFn: async () =>
			normalizeProductsResponse(await productService.listMyProducts({})),
		enabled: Boolean(token) && hasSellerAccount,
	});

	const currentProfiler = profileQuery.data || null;
	const seller = currentProfiler?.seller || user?.seller || null;
	const profilerId = currentProfiler?.id || user?.id || "";

	const channelsQuery = useQuery({
		queryKey: ["contact-channels", profilerId],
		queryFn: async () =>
			normalizeContactChannels(
				await contactChannelService.listProfilerChannels(profilerId),
			),
		enabled: Boolean(token) && Boolean(profilerId) && hasSellerAccount,
	});

	const saveMutation = useMutation({
		mutationFn: async (payload: Record<string, string>) =>
			profilerService.updateMySellerProfile(payload),
	});

	const channelMutation = useMutation({
		mutationFn: async ({
			type,
			value,
		}: {
			type: "PHONE_NUMBER" | "WHATSAPP" | "TELEGRAM";
			value: string;
		}) =>
			contactChannelService.upsertContactChannel({
				profilerId,
				type,
				value,
			}),
	});

	const nicheState = nicheQuery.data || normalizeNicheCategoryState({});
	const products = productsQuery.data || [];
	const channels = useMemo(() => channelsQuery.data || [], [channelsQuery.data]);

	const phoneChannel = useMemo(
		() => getLatestChannelByType(channels, "PHONE_NUMBER"),
		[channels],
	);
	const whatsappChannel = useMemo(
		() => getLatestChannelByType(channels, "WHATSAPP"),
		[channels],
	);
	const telegramChannel = useMemo(
		() => getLatestChannelByType(channels, "TELEGRAM"),
		[channels],
	);

	const telegramState = resolveTelegramState(telegramChannel);
	const telegramRegisterLink =
		telegramRegisterLinkOverride || telegramChannel?.telegramRegisterLink || "";
	const telegramContactLink = telegramChannel?.telegramContactLink || "";

	useEffect(() => {
		setFormData({
			shopName: seller?.shopName || "",
			storeAddress: seller?.storeAddress || "",
			note: seller?.note || "",
			profilePicture: seller?.profilePicture || "",
		});
	}, [seller?.note, seller?.profilePicture, seller?.shopName, seller?.storeAddress]);

	useEffect(() => {
		setContactForm({
			PHONE_NUMBER: phoneChannel?.value || "",
			WHATSAPP: whatsappChannel?.value || "",
			TELEGRAM: telegramChannel?.value || "",
		});
	}, [phoneChannel?.value, telegramChannel?.value, whatsappChannel?.value]);

	useEffect(() => {
		if (telegramChannel?.profilerTelegramChatId) {
			setTelegramRegisterLinkOverride("");
		}
	}, [telegramChannel?.profilerTelegramChatId]);

	const loading =
		Boolean(token) &&
		hasSellerAccount &&
		(profileQuery.isLoading || nicheQuery.isLoading || productsQuery.isLoading);

	const loadError = profileQuery.error
		? getApiErrorMessage(profileQuery.error, "Could not load seller profile.")
		: nicheQuery.error
			? getApiErrorMessage(
					nicheQuery.error,
					"Could not load approved niche categories.",
				)
			: productsQuery.error
				? getApiErrorMessage(productsQuery.error, "Could not load products.")
				: "";

	const channelsErrorMessage = channelsQuery.error
		? getApiErrorMessage(
				channelsQuery.error,
				"Could not load contact channels.",
			)
		: "";

	const handleProfileChange = (name, value) => {
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleContactChange = (type, value) => {
		setContactForm((prev) => ({ ...prev, [type]: value }));
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!hasSellerAccount || !seller || saveMutation.isPending) {
			return;
		}

		const payload = buildProfilePatch(seller, formData);

		if (Object.keys(payload).length === 0) {
			setSubmitSuccess("");
			setSubmitError("No seller profile changes detected.");
			return;
		}

		setSubmitError("");
		setSubmitSuccess("");

		try {
			await saveMutation.mutateAsync(payload);
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["profiler-account-me"] }),
				queryClient.invalidateQueries({ queryKey: ["my-niche-categories"] }),
				refreshProfile(),
			]);
			setSubmitSuccess("Seller profile and note updated successfully.");
		} catch (error) {
			setSubmitError(
				getApiErrorMessage(error, "Could not update seller profile."),
			);
		}
	};

	const handleSaveChannel = async (type) => {
		if (!profilerId || channelMutation.isPending) {
			return;
		}

		const value = contactForm[type]?.trim() || "";

		if (!value) {
			setContactSuccess("");
			setContactError(`Enter a value before saving ${formatChannelName(type)}.`);
			return;
		}

		setActiveChannelType(type);
		setContactError("");
		setContactSuccess("");

		try {
			const response = await channelMutation.mutateAsync({ type, value });

			if (type === "TELEGRAM") {
				setTelegramRegisterLinkOverride(extractTelegramRegisterLink(response));
			}

			await queryClient.invalidateQueries({
				queryKey: ["contact-channels", profilerId],
			});

			setContactSuccess(
				type === "TELEGRAM"
					? "Telegram contact channel saved. Finish the connect flow to complete verification."
					: `${formatChannelName(type)} saved successfully.`,
			);
		} catch (error) {
			setContactError(
				getApiErrorMessage(error, `Could not save ${formatChannelName(type)}.`),
			);
		} finally {
			setActiveChannelType("");
		}
	};

	const handleRefreshTelegram = async () => {
		if (!profilerId) {
			return;
		}

		setIsRefreshingTelegram(true);
		setContactError("");
		setContactSuccess("");

		try {
			const result = await channelsQuery.refetch();
			const refreshedChannels = result.data || [];
			const refreshedTelegramChannel = getLatestChannelByType(
				refreshedChannels,
				"TELEGRAM",
			);

			if (refreshedTelegramChannel?.profilerTelegramChatId) {
				setContactSuccess("Telegram verification completed successfully.");
			} else {
				setContactSuccess(
					"Telegram status refreshed. Verification is still pending.",
				);
			}
		} catch (error) {
			setContactError(
				getApiErrorMessage(error, "Could not refresh Telegram verification."),
			);
		} finally {
			setIsRefreshingTelegram(false);
		}
	};

	if (!hasSellerAccount) {
		return (
			<div className="space-y-6 max-w-5xl mx-auto">
				<div className="rounded-3xl border border-dashed border-gray-300 bg-white p-10 text-center dark:border-gray-700 dark:bg-gray-900">
					<Store size={40} className="mx-auto text-gray-300 dark:text-gray-700" />
					<h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">
						Seller profile unavailable
					</h2>
					<p className="mx-auto mt-2 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
						This screen only becomes available after seller provisioning. Finish
						seller onboarding from the tenant dashboard and return here once the
						seller account exists on the profiler record.
					</p>
					<button
						type="button"
						onClick={() => navigate("/tenant")}
						className="mt-6 inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500">
						Open tenant dashboard <ArrowRight size={15} />
					</button>
				</div>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="min-h-[60vh] flex items-center justify-center">
				<div className="text-center">
					<Loader2 size={40} className="mx-auto animate-spin text-violet-500" />
					<p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
						Loading seller profile...
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6 max-w-6xl mx-auto">
			<section className="overflow-hidden rounded-3xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
				<div className="h-28 bg-gradient-to-r from-violet-900/60 via-indigo-900/45 to-slate-900/45" />

				<div className="-mt-10 px-6 pb-6">
					<div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
						<div className="flex items-end gap-4">
							<div className="relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-3xl border-4 border-white bg-white shadow-lg dark:border-gray-900 dark:bg-gray-950">
								{formData.profilePicture ? (
									<img
										src={formData.profilePicture}
										alt={seller?.shopName || "Seller profile"}
										className="h-full w-full object-cover"
									/>
								) : (
									<div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-violet-500 to-indigo-600 text-2xl font-bold text-white">
										{user?.initials || "PF"}
									</div>
								)}
								{user?.verified && (
									<div className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-emerald-500 dark:border-gray-900">
										<BadgeCheck size={15} className="text-white" />
									</div>
								)}
							</div>

							<div className="pb-1">
								<div className="flex flex-wrap items-center gap-2">
									<h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
										{seller?.shopName || user?.name || "Seller account"}
									</h2>
									{user?.verified && (
										<span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300">
											<BadgeCheck size={12} /> Verified seller
										</span>
									)}
								</div>
								<p className="mt-1 text-sm font-medium text-violet-500 dark:text-violet-300">
									{seller?.plan?.name || user?.planName || user?.plan || "Seller plan pending"}
								</p>
								<p className="mt-3 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
									This screen manages seller profile details, seller note, and
									customer contact channels using the current logged-in profiler
									record.
								</p>
							</div>
						</div>

						<button
							type="button"
							onClick={() => navigate("/dashboard/incoming-orders")}
							className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500">
							Incoming orders <ArrowRight size={15} />
						</button>
					</div>
				</div>
			</section>

			{seller?.isBanned && (
				<div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
					<div className="flex items-start gap-3">
						<ShieldAlert size={18} className="mt-0.5 flex-shrink-0" />
						<div>
							<p className="font-semibold">Seller restrictions are active</p>
							<p className="mt-1">
								{seller?.banReason ||
									"Product and category changes remain blocked until this restriction is removed."}
							</p>
						</div>
					</div>
				</div>
			)}

			{(loadError || submitError || submitSuccess) && (
				<div
					className={`rounded-2xl border p-4 text-sm ${submitSuccess
							? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
							: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200"
						}`}>
					{submitSuccess || submitError || loadError}
				</div>
			)}

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
				<ProfileStat label="Products" value={String(products.length)} />
				<ProfileStat
					label="Approved Categories"
					value={String(nicheState.selectedCategories.length)}
				/>
				<ProfileStat
					label="Market"
					value={seller?.market?.name || user?.marketName || "Not assigned"}
				/>
				<ProfileStat
					label="Status"
					value={seller?.isBanned ? "Restricted" : "Active"}
				/>
			</div>

			<div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.15fr_0.85fr]">
				<section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
					<div className="flex items-center justify-between gap-4">
						<div>
							<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
								Seller note and profile settings
							</h3>
							<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
								Updates call the supported seller profile endpoint directly.
							</p>
						</div>
					</div>

					<form onSubmit={handleSubmit} className="mt-6 space-y-5">
						<Field label="Shop Name">
							<input
								value={formData.shopName}
								onChange={(event) =>
									handleProfileChange("shopName", event.target.value)
								}
								className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
								placeholder="Enter shop name"
							/>
						</Field>

						<Field label="Store Address">
							<input
								value={formData.storeAddress}
								onChange={(event) =>
									handleProfileChange("storeAddress", event.target.value)
								}
								className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
								placeholder="Enter store address"
							/>
						</Field>

						<Field
							label="Seller Note"
							helper="This value is stored on seller.note and can be used as your customer-facing seller note."
						>
							<textarea
								value={formData.note}
								onChange={(event) => handleProfileChange("note", event.target.value)}
								className="min-h-28 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
								placeholder="Add your current seller note"
							/>
						</Field>

						<Field
							label="Profile Picture URL"
							helper="Optional image URL served by your backend or CDN."
						>
							<input
								value={formData.profilePicture}
								onChange={(event) =>
									handleProfileChange("profilePicture", event.target.value)
								}
								className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
								placeholder="https://..."
							/>
						</Field>

						<div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
							<button
								type="button"
								onClick={() =>
									setFormData({
										shopName: seller?.shopName || "",
										storeAddress: seller?.storeAddress || "",
										note: seller?.note || "",
										profilePicture: seller?.profilePicture || "",
									})
								}
								className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
								Reset
							</button>
							<button
								type="submit"
								disabled={saveMutation.isPending}
								className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-60">
								{saveMutation.isPending && (
									<Loader2 size={15} className="animate-spin" />
								)}
								<Save size={15} /> Save settings
							</button>
						</div>
					</form>
				</section>

				<section className="space-y-4">
					<article className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
						<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
							Seller details
						</h3>
						<div className="mt-5 space-y-3">
							<DetailRow
								label="Market"
								value={seller?.market?.name || user?.marketName || "Not assigned"}
								icon={MapPin}
							/>
							<DetailRow
								label="Phone"
								value={user?.phone || user?.phoneNumber || "-"}
								icon={Phone}
							/>
							<DetailRow
								label="Plan"
								value={
									seller?.plan?.name || user?.planName || user?.plan || "Not resolved"
								}
								icon={Store}
							/>
							<DetailRow
								label="Product limit"
								value={String(seller?.plan?.maxProducts || nicheState.maxProducts || 0)}
								icon={Package}
							/>
						</div>
					</article>

					<article className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
						<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
							Approved categories
						</h3>
						<div className="mt-4 flex flex-wrap gap-2">
							{nicheState.selectedCategories.length > 0 ? (
								nicheState.selectedCategories.map((category) => (
									<span
										key={category.id}
										className="rounded-full bg-violet-50 px-3 py-1.5 text-sm font-medium text-violet-700 dark:bg-violet-500/10 dark:text-violet-200">
										{category.name}
									</span>
								))
							) : (
								<p className="text-sm text-gray-500 dark:text-gray-400">
									No approved niche categories assigned yet.
								</p>
							)}
						</div>
					</article>

					<article className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
						<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
							Profile picture preview
						</h3>
						<div className="mt-4 flex items-center gap-4 rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
							<div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-white dark:bg-gray-900">
								{formData.profilePicture ? (
									<img
										src={formData.profilePicture}
										alt="Profile preview"
										className="h-full w-full object-cover"
									/>
								) : (
									<ImageIcon size={24} className="text-gray-400" />
								)}
							</div>
							<p className="text-sm text-gray-500 dark:text-gray-400">
								{formData.profilePicture
									? "The current image URL will be sent to the seller profile endpoint when you save."
									: "No profile picture URL has been set yet."}
							</p>
						</div>
					</article>
				</section>
			</div>

			<section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.05fr_0.95fr]">
				<article className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
					<div className="flex items-start justify-between gap-4">
						<div>
							<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
								Contact channels
							</h3>
							<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
								Phone and WhatsApp are saved with the contact-channel upsert endpoint.
							</p>
						</div>
					</div>

					{(contactError || contactSuccess || channelsErrorMessage) && (
						<div
							className={`mt-5 rounded-2xl border p-4 text-sm ${contactSuccess
									? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
									: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200"
								}`}>
							{contactSuccess || contactError || channelsErrorMessage}
						</div>
					)}

					{channelsQuery.isLoading ? (
						<div className="mt-6 flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-400">
							<Loader2 size={16} className="animate-spin text-violet-500" />
							Loading contact channels...
						</div>
					) : (
						<div className="mt-6 space-y-5">
							<ContactChannelField
								label="Phone Number"
								helper="Use the phone number customers should use to reach this seller account."
								icon={Phone}
								value={contactForm.PHONE_NUMBER}
								placeholder={user?.phone || user?.phoneNumber || "e.g. 08030000000"}
								onChange={(value) => handleContactChange("PHONE_NUMBER", value)}
								onSave={() => handleSaveChannel("PHONE_NUMBER")}
								isSaving={channelMutation.isPending && activeChannelType === "PHONE_NUMBER"}
							/>

							<ContactChannelField
								label="WhatsApp"
								helper="This value is saved as the WhatsApp contact channel for the current profiler."
								icon={MessageCircle}
								value={contactForm.WHATSAPP}
								placeholder="e.g. 08030000000 or https://wa.me/..."
								onChange={(value) => handleContactChange("WHATSAPP", value)}
								onSave={() => handleSaveChannel("WHATSAPP")}
								isSaving={channelMutation.isPending && activeChannelType === "WHATSAPP"}
							/>
						</div>
					)}
				</article>

				<article className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
					<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
						<div>
							<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
								Telegram channel
							</h3>
							<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
								Save Telegram first, then complete the register-link flow and refresh verification.
							</p>
						</div>
						<span
							className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold ${TELEGRAM_STATUS_STYLES[telegramState.tone]}`}>
							{telegramState.tone === "emerald" ? (
								<CheckCircle size={12} />
							) : telegramState.tone === "amber" ? (
								<AlertCircle size={12} />
							) : (
								<Send size={12} />
							)}
							{telegramState.label}
						</span>
					</div>

					<div className="mt-6 space-y-5">
						<Field
							label="Telegram Value"
							helper="Save the Telegram contact value you want tied to this seller account."
						>
							<input
								value={contactForm.TELEGRAM}
								onChange={(event) =>
									handleContactChange("TELEGRAM", event.target.value)
								}
								className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
								placeholder="e.g. @ShopOwnaa_seller"
							/>
						</Field>

						<div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
							<button
								type="button"
								onClick={() => handleSaveChannel("TELEGRAM")}
								disabled={channelMutation.isPending && activeChannelType === "TELEGRAM"}
								className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-60">
								{channelMutation.isPending && activeChannelType === "TELEGRAM" ? (
									<Loader2 size={15} className="animate-spin" />
								) : (
									<Send size={15} />
								)}
								Save Telegram
							</button>

							{telegramRegisterLink && !telegramChannel?.profilerTelegramChatId && (
								<button
									type="button"
									onClick={() =>
										window.open(telegramRegisterLink, "_blank", "noopener,noreferrer")
									}
									className="inline-flex items-center justify-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2.5 text-sm font-semibold text-violet-700 transition hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-300 dark:hover:bg-violet-950/50">
									Connect Telegram <ArrowRight size={15} />
								</button>
							)}

							{(telegramState.label === "Pending verification" ||
								telegramChannel?.telegramRegisterLink) && (
								<button
									type="button"
									onClick={handleRefreshTelegram}
									disabled={isRefreshingTelegram || channelsQuery.isFetching}
									className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200 dark:hover:bg-gray-800">
									{isRefreshingTelegram ? (
										<Loader2 size={15} className="animate-spin" />
									) : (
										<RefreshCw size={15} />
									)}
									Refresh verification
								</button>
							)}
						</div>

						<div className="space-y-3 rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
							<TelegramInfoRow
								label="Customer-facing link"
								value={telegramContactLink || "Not available yet"}
								isLink={Boolean(telegramContactLink)}
							/>
							<TelegramInfoRow
								label="Register link"
								value={telegramRegisterLink || "Save Telegram to receive a connect link"}
								isLink={Boolean(telegramRegisterLink)}
							/>
							<TelegramInfoRow
								label="Telegram chat ID"
								value={telegramChannel?.profilerTelegramChatId || "Pending verification"}
							/>
						</div>
					</div>
				</article>
			</section>
		</div>
	);
}

function formatChannelName(type) {
	if (type === "PHONE_NUMBER") return "phone number";
	if (type === "WHATSAPP") return "WhatsApp";
	if (type === "TELEGRAM") return "Telegram";
	return "channel";
}

function ProfileStat({ label, value }) {
	return (
		<article className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
			<p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
				{label}
			</p>
			<p className="mt-3 text-2xl font-bold text-gray-900 dark:text-gray-100">
				{value}
			</p>
		</article>
	);
}

function Field({ label, helper = "", children }) {
	return (
		<label className="block">
			<span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
				{label}
			</span>
			{children}
			{helper && (
				<span className="mt-2 block text-xs text-gray-500 dark:text-gray-400">
					{helper}
				</span>
			)}
		</label>
	);
}

function DetailRow({ label, value, icon: Icon }) {
	return (
		<div className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
			<div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-white text-violet-600 dark:bg-gray-900 dark:text-violet-300">
				<Icon size={16} />
			</div>
			<div>
				<p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
					{label}
				</p>
				<p className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
					{value}
				</p>
			</div>
		</div>
	);
}

function ContactChannelField({
	label,
	helper,
	icon: Icon,
	value,
	placeholder,
	onChange,
	onSave,
	isSaving,
}) {
	return (
		<div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
			<div className="flex items-start gap-3">
				<div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-violet-600 dark:bg-gray-900 dark:text-violet-300">
					<Icon size={18} />
				</div>
				<div className="flex-1">
					<p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
						{label}
					</p>
					<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
						{helper}
					</p>
					<div className="mt-4 flex flex-col gap-3 sm:flex-row">
						<input
							value={value}
							onChange={(event) => onChange(event.target.value)}
							placeholder={placeholder}
							className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
						/>
						<button
							type="button"
							onClick={onSave}
							disabled={isSaving}
							className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-60">
							{isSaving && <Loader2 size={15} className="animate-spin" />}
							Save
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

function TelegramInfoRow({ label, value, isLink = false }) {
	return (
		<div>
			<p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
				{label}
			</p>
			{isLink ? (
				<a
					href={value}
					target="_blank"
					rel="noreferrer"
					className="mt-2 inline-flex break-all text-sm font-semibold text-violet-600 transition hover:text-violet-500 dark:text-violet-300 dark:hover:text-violet-200">
					{value}
				</a>
			) : (
				<p className="mt-2 break-all text-sm font-semibold text-gray-900 dark:text-gray-100">
					{value}
				</p>
			)}
		</div>
	);
}
